import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { naira, formatApiError } from "../lib/format";
import { Card, Loading, KPI } from "../components/UI";
import { Download, FileText, BarChart3, TrendingUp, TrendingDown, Loader2 } from "lucide-react";

const REPORTS = [
  { key: "occupancy", label: "Occupancy Report" },
  { key: "tenants", label: "Tenants List" },
  { key: "payments", label: "Payments / Rent Collection" },
  { key: "leases", label: "Leases" },
  { key: "maintenance", label: "Maintenance" },
  { key: "pricing", label: "Pricing History" },
];

export default function Reports() {
  const [pl, setPl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    api.get("/reports/profit-loss").then(r => setPl(r.data)).catch(() => setPl(null)).finally(() => setLoading(false));
  }, []);

  const download = async (key, label) => {
    setDownloading(key);
    try {
      const res = await api.get(`/reports/export.csv?report=${key}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${key}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${label} downloaded`);
    } catch (err) {
      toast.error(formatApiError(err) || "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Reports</h1>
        <p className="text-[#5E7166] text-sm">Download CSV reports for all key data.</p>
      </div>

      {loading ? <Loading/> : pl && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPI label="Total Income" value={naira(pl.income)} icon={TrendingUp} accent/>
          <KPI label="Total Expenses" value={naira(pl.expenses)} icon={TrendingDown}/>
          <KPI label="Net Profit" value={naira(pl.net)} icon={BarChart3} accent={pl.net >= 0}/>
        </div>
      )}

      <Card title="Available Reports">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {REPORTS.map(r => {
            const busy = downloading === r.key;
            return (
              <button
                key={r.key}
                onClick={() => download(r.key, r.label)}
                disabled={busy}
                className="card-modav p-4 hover:border-[#C9A227] flex items-center justify-between group text-left disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid={`report-${r.key}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#E8F5EE] text-[#0E5C3A] rounded-lg flex items-center justify-center"><FileText size={18}/></div>
                  <div>
                    <div className="font-semibold text-sm">{r.label}</div>
                    <div className="text-xs text-[#5E7166]">{busy ? "Preparing…" : "Download CSV"}</div>
                  </div>
                </div>
                {busy
                  ? <Loader2 size={18} className="text-[#C9A227] animate-spin"/>
                  : <Download size={18} className="text-[#5E7166] group-hover:text-[#C9A227] transition-colors"/>}
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
